// @flow
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/styles';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import CloudOutlinedIcon from '@material-ui/icons/CloudOutlined';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import MenuOutlinedIcon from '@material-ui/icons/MenuOutlined';

import AvaLogoImg from '../../../assets/images/logo.png';

import ServerSettings from './ServerSettings';
import SidebarItem from './SidebarItem';
import JoinConversationButton from '../JoinConversation/Button';
import StartCaptionsButton from './StartCaptionsButton';
import ContactSupportIcon from '../Icons/ContactSupport';
import AccountSettingsIcon from '../Icons/Sidebar/AccountSettings';
import ScribeIcon from '../Icons/Scribe';
import TranscriptIcon from '../Icons/Sidebar/Transcript';

import { toggleSideBar } from '../../actions';

import { getIsMac } from '../../utils/electron';
import { show } from '../../utils/intercom';

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    transform: 'translateX(0) !important',
    transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1) 0ms !important',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  sidebarClosedContent: {
    justifyContent: 'center !important',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 76,
    paddingLeft: props => (props.sidebarOpen ? 24 : 0),
    paddingRight: props => (props.sidebarOpen ? 16 : 0),
  },
  logoContainer: {
    maxWidth: 100,
  },
  logo: {
    height: '100%',
    width: '100%',
    cursor: 'pointer',
  },
  menuContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  options: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      width: '0 !important',
    },
  },
  conversation: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 25,
  },
  divider: {
    marginTop: 24,
    marginRight: 20,
    marginBottom: 24,
    marginLeft: 20,
    backgroundColor: theme.palette.type === 'light' ? theme.palette.ava.grey5 : theme.palette.ava.text.dark1,
  },
  list: {
    flex: 1,
  },
  link: {
    color: 'currentColor',
    '&:hover': {
      color: 'currentColor',
      textDecoration: 'none',
    },
  },
  disabled: {
    cursor: 'default',
  },
  footer: {
    minHeight: 120,
  },
}));

interface Props {
  className?: string;
  history: Object;
  currentPlatform: string;
  displayable: Object;
  sidebarOpen: boolean;
  toggleSideBar: Function;
}

const HomeView = ({ className, history, currentPlatform, displayable, sidebarOpen, toggleSideBar }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles({ sidebarOpen });
  const theme = useTheme();

  const isMac = getIsMac();
  let headerTop = 0;
  if (window.isElectron) {
    headerTop = isMac ? 28 : 32;
  }

  const getSidebarWidth = () => {
    if (sidebarOpen) return 272;
    return 90;
  };

  const {
    location: { pathname },
  } = history;

  const onAvaLogoClick = async (e, product) => {
    e.preventDefault();
    if (!pathname.includes('/web/transcript')) {
      history.push('/web/transcript');
    }
  };

  return (
    <Drawer
      className={classnames('invisible-scrollbar', classes.sidebar, className)}
      classes={{
        paper: classes.drawerPaper,
      }}
      style={{
        width: getSidebarWidth(),
      }}
      PaperProps={{
        style: {
          top: window.isElectron ? headerTop : 0,
          height: window.isElectron ? 'calc(100% - 10px)' : '100%',
          width: getSidebarWidth(),
          visibility: 'visible',
          background:
            theme.palette.type === 'light'
              ? 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%, rgba(255, 255, 255, 0) 100%), rgba(255, 255, 255, 0.6)'
              : theme.palette.ava.dark4,
          borderRight: `2px solid ${
            theme.palette.type === 'light' ? theme.palette.ava.offBlue1 : theme.palette.ava.dark1
          }`,
        },
      }}
      open={sidebarOpen}
      anchor="left"
      variant="persistent"
    >
      <div className={classes.container}>
        <section
          className={classnames(classes.header, {
            [classes.sidebarClosedContent]: !sidebarOpen,
          })}
        >
          {sidebarOpen && (
            <div className={classes.logoContainer}>
              <img className={classes.logo} src={AvaLogoImg} alt="ava logo" onClick={onAvaLogoClick} />
            </div>
          )}
          <div className={classes.menuContainer}>
            {sidebarOpen && (
              <ArrowBackIosIcon
                style={{
                  position: 'absolute',
                  left: -8,
                  fontSize: 16,
                  cursor: 'pointer',
                  color: theme.palette.text.primary,
                }}
                onClick={toggleSideBar}
              />
            )}
            <MenuOutlinedIcon
              style={{
                fontSize: 34,
                cursor: 'pointer',
                color: theme.palette.text.primary,
              }}
              onClick={toggleSideBar}
            />
          </div>
        </section>
        <div className={classes.options}>
          <section className={classes.conversation}>
            <StartCaptionsButton />
            <JoinConversationButton />
          </section>
          <Divider className={classes.divider} />
          <List className={classes.list} component="nav" disablePadding>
            {displayable.scribeDashboard && (
              <Link
                to="/web/scribe-dashboard"
                className={classnames(classes.link, {
                  [classes.disabled]: pathname.includes('/web/scribe-dashboard'),
                })}
              >
                <SidebarItem
                  active={pathname.includes('/web/scribe-dashboard')}
                  icon={
                    <ScribeIcon
                      active={pathname.includes('/web/scribe-dashboard')}
                      color={pathname.includes('/web/scribe-dashboard') ? 'white' : ''}
                    />
                  }
                  text={t('sidebar.scribeDashboard')}
                  to="/web/scribe-dashboard"
                  disableOnClick
                />
              </Link>
            )}
            <Link
              to="/web/conversations"
              className={classnames(classes.link, {
                [classes.disabled]: pathname.includes('/web/conversations'),
              })}
            >
              <SidebarItem
                active={pathname.includes('/web/conversations')}
                icon={
                  <TranscriptIcon
                    active={pathname.includes('/web/conversations')}
                    color={pathname.includes('/web/conversations') ? 'white' : ''}
                  />
                }
                text={t('sidebar.conversations')}
                to="/web/conversations"
                disableOnClick
              />
            </Link>
            <Link
              to="/web/account-settings"
              className={classnames(classes.link, {
                [classes.disabled]: pathname.includes('/web/account-settings'),
              })}
            >
              <SidebarItem
                active={pathname.includes('/web/account-settings')}
                icon={
                  <AccountSettingsIcon
                    active={pathname.includes('/web/account-settings')}
                    color={pathname.includes('/web/account-settings') ? 'white' : ''}
                  />
                }
                text={t('sidebar.accountSettings')}
                disableOnClick
              />
            </Link>
            <ServerSettings />
          </List>
          <Divider className={classes.divider} />
          <List className={classes.footer} disablePadding>
            <SidebarItem icon={<ContactSupportIcon />} text={t('sidebar.contactSupport')} onClick={() => show()} />
          </List>
        </div>
      </div>
    </Drawer>
  );
};

const mapStateToProps = ({ scribeSession: { currentPlatform, displayable, sidebar } }) => ({
  currentPlatform,
  displayable,
  sidebarOpen: sidebar,
});

const mapDispatchToProps = { toggleSideBar };

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HomeView));
